// store/analyticsApi/analyticsSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchDepartmentPerformance,
  fetchTaskRejections,
  fetchTaskTrends,
  fetchTaskComplexity,
  fetchWorkloadDistribution,
  fetchEventEngagement,
  fetchApprovalChainAnalysis,
  fetchTimeAnalysis,
  fetchAnalyticsSummary,
  fetchMyPerformanceDetails,
} from "./analyticsThunk";

const initialState = {
  // Analytics Data
  departmentPerformance: null,
  taskRejections: null,
  taskTrends: null,
  taskComplexity: null,
  workloadDistribution: null,
  eventEngagement: null,
  approvalChainAnalysis: null,
  timeAnalysis: null,
  analyticsSummary: null,
  myPerformanceDetails: null,

  // Loading States
  departmentPerformanceLoading: false,
  taskRejectionsLoading: false,
  taskTrendsLoading: false,
  taskComplexityLoading: false,
  workloadDistributionLoading: false,
  eventEngagementLoading: false,
  approvalChainAnalysisLoading: false,
  timeAnalysisLoading: false,
  analyticsSummaryLoading: false,
  myPerformanceDetailsLoading: false,

  // Error States
  departmentPerformanceError: null,
  taskRejectionsError: null,
  taskTrendsError: null,
  taskComplexityError: null,
  workloadDistributionError: null,
  eventEngagementError: null,
  approvalChainAnalysisError: null,
  timeAnalysisError: null,
  analyticsSummaryError: null,
  myPerformanceDetailsError: null,

  status: "idle",
  error: null,
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    clearAnalyticsData: (state) => {
      return initialState;
    },
    clearAnalyticsErrors: (state) => {
      state.departmentPerformanceError = null;
      state.taskRejectionsError = null;
      state.taskTrendsError = null;
      state.taskComplexityError = null;
      state.workloadDistributionError = null;
      state.eventEngagementError = null;
      state.approvalChainAnalysisError = null;
      state.timeAnalysisError = null;
      state.analyticsSummaryError = null;
      state.myPerformanceDetailsError = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Department Performance
      .addCase(fetchDepartmentPerformance.pending, (state) => {
        state.departmentPerformanceLoading = true;
        state.departmentPerformanceError = null;
      })
      .addCase(fetchDepartmentPerformance.fulfilled, (state, action) => {
        state.departmentPerformanceLoading = false;
        state.departmentPerformance = action.payload;
        state.status = "success";
      })
      .addCase(fetchDepartmentPerformance.rejected, (state, action) => {
        state.departmentPerformanceLoading = false;
        state.departmentPerformanceError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Task Rejections
      .addCase(fetchTaskRejections.pending, (state) => {
        state.taskRejectionsLoading = true;
        state.taskRejectionsError = null;
      })
      .addCase(fetchTaskRejections.fulfilled, (state, action) => {
        state.taskRejectionsLoading = false;
        state.taskRejections = action.payload;
        state.status = "success";
      })
      .addCase(fetchTaskRejections.rejected, (state, action) => {
        state.taskRejectionsLoading = false;
        state.taskRejectionsError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Task Trends
      .addCase(fetchTaskTrends.pending, (state) => {
        state.taskTrendsLoading = true;
        state.taskTrendsError = null;
      })
      .addCase(fetchTaskTrends.fulfilled, (state, action) => {
        state.taskTrendsLoading = false;
        state.taskTrends = action.payload;
        state.status = "success";
      })
      .addCase(fetchTaskTrends.rejected, (state, action) => {
        state.taskTrendsLoading = false;
        state.taskTrendsError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Task Complexity
      .addCase(fetchTaskComplexity.pending, (state) => {
        state.taskComplexityLoading = true;
        state.taskComplexityError = null;
      })
      .addCase(fetchTaskComplexity.fulfilled, (state, action) => {
        state.taskComplexityLoading = false;
        state.taskComplexity = action.payload;
        state.status = "success";
      })
      .addCase(fetchTaskComplexity.rejected, (state, action) => {
        state.taskComplexityLoading = false;
        state.taskComplexityError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Workload Distribution
      .addCase(fetchWorkloadDistribution.pending, (state) => {
        state.workloadDistributionLoading = true;
        state.workloadDistributionError = null;
      })
      .addCase(fetchWorkloadDistribution.fulfilled, (state, action) => {
        state.workloadDistributionLoading = false;
        state.workloadDistribution = action.payload;
        state.status = "success";
      })
      .addCase(fetchWorkloadDistribution.rejected, (state, action) => {
        state.workloadDistributionLoading = false;
        state.workloadDistributionError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Event Engagement
      .addCase(fetchEventEngagement.pending, (state) => {
        state.eventEngagementLoading = true;
        state.eventEngagementError = null;
      })
      .addCase(fetchEventEngagement.fulfilled, (state, action) => {
        state.eventEngagementLoading = false;
        state.eventEngagement = action.payload;
        state.status = "success";
      })
      .addCase(fetchEventEngagement.rejected, (state, action) => {
        state.eventEngagementLoading = false;
        state.eventEngagementError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Approval Chain Analysis
      .addCase(fetchApprovalChainAnalysis.pending, (state) => {
        state.approvalChainAnalysisLoading = true;
        state.approvalChainAnalysisError = null;
      })
      .addCase(fetchApprovalChainAnalysis.fulfilled, (state, action) => {
        state.approvalChainAnalysisLoading = false;
        state.approvalChainAnalysis = action.payload;
        state.status = "success";
      })
      .addCase(fetchApprovalChainAnalysis.rejected, (state, action) => {
        state.approvalChainAnalysisLoading = false;
        state.approvalChainAnalysisError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Time Analysis
      .addCase(fetchTimeAnalysis.pending, (state) => {
        state.timeAnalysisLoading = true;
        state.timeAnalysisError = null;
      })
      .addCase(fetchTimeAnalysis.fulfilled, (state, action) => {
        state.timeAnalysisLoading = false;
        state.timeAnalysis = action.payload;
        state.status = "success";
      })
      .addCase(fetchTimeAnalysis.rejected, (state, action) => {
        state.timeAnalysisLoading = false;
        state.timeAnalysisError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Analytics Summary
      .addCase(fetchAnalyticsSummary.pending, (state) => {
        state.analyticsSummaryLoading = true;
        state.analyticsSummaryError = null;
      })
      .addCase(fetchAnalyticsSummary.fulfilled, (state, action) => {
        state.analyticsSummaryLoading = false;
        state.analyticsSummary = action.payload;
        state.status = "success";
      })
      .addCase(fetchAnalyticsSummary.rejected, (state, action) => {
        state.analyticsSummaryLoading = false;
        state.analyticsSummaryError = action.payload || action.error.message;
        state.status = "failed";
      })

      // My Performance Details
      .addCase(fetchMyPerformanceDetails.pending, (state) => {
        state.myPerformanceDetailsLoading = true;
        state.myPerformanceDetailsError = null;
      })
      .addCase(fetchMyPerformanceDetails.fulfilled, (state, action) => {
        state.myPerformanceDetailsLoading = false;
        state.myPerformanceDetails = action.payload;
        state.status = "success";
      })
      .addCase(fetchMyPerformanceDetails.rejected, (state, action) => {
        state.myPerformanceDetailsLoading = false;
        state.myPerformanceDetailsError = action.payload || action.error.message;
        state.status = "failed";
      });
  },
});

export const { clearAnalyticsData, clearAnalyticsErrors } = analyticsSlice.actions;
export default analyticsSlice.reducer;
